import { z } from "zod";

import type { AccountType } from "@/types";

export const emailSchema = z.object({
	email: z
		.string()
		.email({ message: "Must be a valid email eg. demo@demo.com" })
		.refine(s => !s.includes(" "), "No Spaces!")
});

export type FormSchema = z.infer<ReturnType<typeof AuthFormSchema>> & {
	userType: AccountType;
};
export const userTypeSchema = z.enum(["BUYER", "VENDOR"], {
	required_error: "Account type is required"
});

export type TEmailSchema = z.infer<typeof emailSchema>;

export const passwordSchema = z
	.string()
	.min(8, { message: "Password must be at least 8 characters." })
	.refine(s => !s.includes(" "), "No Spaces!")
	.refine(password => /[A-Z]/.test(password), {
		message: "Password must contain at least 1 Uppercase letter."
	})
	.refine(password => /[a-z]/.test(password), {
		message: "Password must contain at least 1 lowercase letter."
	})
	.refine(password => /\d/.test(password), { message: "Password must contain at least one number" })
	.refine(password => /[!@#$%^&*(),.?":{}|<>]/.test(password), {
		message: "Password must contain at least one special character eg. !@#$%^&*(),.?:{}|<>"
	});

export type AuthFormType = "sign-in" | "sign-up";

function baseStringSchema(field: string, regex: RegExp = /\d/) {
	return z.string({
		required_error: `${field} is required`
	}).min(3, {
		message: `${field} must be at least 3 characters.`
	}).refine(s => !s.includes(" "), "No Spaces!").refine(s => !regex.test(s), "Numbers are not allowed");
}
export function AuthFormSchema(type: "sign-in" | "sign-up") {
	const baseSchema = {
		email: emailSchema.shape.email,
		password: type === "sign-up"
			? passwordSchema
			: z.string({
					required_error: "Password is required"
				}).min(2, { message: "Password must be at least 8 characters." })
	};

	if (type === "sign-up") {
		return z.object({
			...baseSchema,
			firstName: baseStringSchema("Firstname"),
			lastName: baseStringSchema("Lastname"),
			username: baseStringSchema("Username", /\*/),
			phoneNumber: z
				.string({ required_error: "Phone number is required" })
				.min(10, { message: "Phone number must be at least 10 digits" })
				.max(15, { message: "Phone number must be at most 15 digits" })
				.regex(/^\+?(\d{10,15})$/, {
					message: "Enter a valid phone number (e.g. +2348012345678)"
				}),
			referrerCode: z.string().optional(),
			tos: z.boolean().refine(value => value === true, {
				message: "You must accept the terms of service"
			}),
			confirmPassword: z.string({
				required_error: "Confirm password is required"
			}).optional()
		}).refine(data => data.password === data.confirmPassword, {
			message: "Passwords don't match",
			path: ["confirmPassword"]
		});
	}

	return z.object({
		...baseSchema
	});
}
